import { useEffect, useState, createRef, useCallback } from 'react';
import * as ScanditSDK from 'scandit-sdk';
import Scanner from '../../../../components/Scanner';
import keyboard from '../../../../styles/img/account/keyboard.svg';
import closeArrow from '../../../../styles/img/account/closeArrow.svg';
import PrimaryButton from '../../../../components/button/PrimaryButton';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import Config from '../../../../axios/Config';
import {
  ACCOUNT,
  ADD_FID_CARD,
  ADD_FID_SCAN,
  NUMBER_SCREEN,
  SCAN, USER_FID_CARD,
  USER_FID_CARD_DETAIL,
} from '../../../../constants/routes';
import {
  notifyError,
  notifySuccess,
  notifyWarning,
} from '../../../../utils/Notify';
import { IoMdCheckmark } from 'react-icons/io';
import { addFidCard } from '../../../../actions/userFidelityActions';
import { BarcodePicker } from 'scandit-sdk';
import ScannerTest from '../../../../scanner/ScannerTest';
import { isAppPWA } from '../../../../utils/helper-functions';
import { IoIosArrowBack } from 'react-icons/io';
import ScanditInit from '../../../../components/Scandit/Sandit';
import successLottie from '../../../../styles/lottie/success.json';
import Lottie from 'lottie-react';
import { ReloadPage } from '../../../../components/tile/ReloadPage';
import { vidercache } from '../../../../utils/ClearCache';
import { useFunnelTracking } from '../../../../posthog/funnelTracking';

const SEARCH_AREA_CONFIG = { x: 0.025, y: 0.3, width: 0.95, height: 0.3 };
const PWA_VIEWFINDER_AREA = {
  x: 0.2,
  y: 0.23,
  width: 0.6,
  height: 0.1,
};
const ENABLED_SYMBOLOGIES = ['ean8', 'ean13', 'upca', 'code128', 'qr'];
const CODE_DUPLICATE_FILTER = 2500;
const MAX_CODES_PER_FRAME = 1;
const TARGET_SCANNING_FPS = 10;
const RELOAD_BUTTON_DELAY = 6000;
const SUCCESS_SCREEN_DURATION = 2400;
const SPINNER_ELEMENTS = Array.from({ length: 12 }, (_, index) => `spinner-${index}-${Date.now()}`);

const FidScanScreen = ({ goToCardScreen, companyLogo }) => {
  const [fidCardNum, setFidCardNum] = useState('');
  const [manual, setManual] = useState(false);
  const { prevPage } = useLocation().state || { prevPage: null };
  const companyImage = useLocation().state?.companyLogo;
  const { store } = useSelector((state) => state.store);
  const navigate = useNavigate();
  const reloadPageRef = createRef();
  const focusref = createRef();
  const [seeReloadButton, setSeeReloadButton] = useState(false);
  const axiosInstance = Config();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const { cartItems } = useSelector((state) => state.cart);
  const [recaptchaResultToken, setRecaptchaResultToken] = useState('');
  const [fidelityCardId, setFidelityCardId] = useState(undefined);
  const [isPaused, setIsPaused] = useState(false);
  const [popup, setPopup] = useState(false);
  const [isScanditLoaded, setIsScanditLoaded] = useState(false);
  const [fidCardSuccess, setFidCardSuccess] = useState(false);
  const [isLoadingScanner, setIsLoadingScanner] = useState(true);
  const [addFidCardLoading, setAddFidCardLoading] = useState(false);
  const isPWA = isAppPWA();
  const { trackPageViewFidScan } = useFunnelTracking();
  const { state } = useLocation();

  const onAddFidCardSuccess = useCallback((fidCardNum, data) => {
    if (data?.fidCard[0]?.fidelityInfo?.statutIdentification === 'OK') {
      setIsPaused(true);
      if (user?._id) {
        if (prevPage === ACCOUNT || prevPage === ADD_FID_CARD) {
          navigate(USER_FID_CARD, { state: { fidCardNum: fidCardNum } });
        } else {
          navigate(SCAN, { state: { fidCardNum: fidCardNum, previousRoute: prevPage } });
        }
        notifySuccess('Carte ajoutée !');
      } else {
        setPopup(true);
      }
    } else {
      notifyError('Erreur lors de l\'ajout de la carte');
    }
  }, [user?._id, prevPage, navigate]);

  const processFidelityCard = useCallback((cardNumber) => {
    if (!cardNumber) {
      notifyWarning('Il faut saisir un numéro de carte de fidélité.');
      return;
    }
    dispatch(
      addFidCard(
        axiosInstance,
        user,
        cartItems,
        cardNumber,
        store?.id,
        (data) => onAddFidCardSuccess(cardNumber, data),
        setAddFidCardLoading,
        state?.companyId,
      ),
    );
  }, [axiosInstance, user, cartItems, dispatch, onAddFidCardSuccess]);

  const handleManualSubmit = useCallback(() => {
    processFidelityCard(fidCardNum);
  }, [fidCardNum, processFidelityCard]);

  const getScanSettings = useCallback(() => {
    return new ScanditSDK.ScanSettings({
      enabledSymbologies: ENABLED_SYMBOLOGIES,
      codeDuplicateFilter: CODE_DUPLICATE_FILTER,
      searchArea: SEARCH_AREA_CONFIG,
      maxNumberOfCodesPerFrame: MAX_CODES_PER_FRAME,
    });
  }, []);

  const navigateType = useCallback((type) => {
    setPopup(false);

    if (type === 'account') {
      navigate(NUMBER_SCREEN, { state: { prevPage: ADD_FID_SCAN } });
      return;
    }

    setFidCardSuccess(true);
    setTimeout(() => {
      if (user?._id) {
        navigate(USER_FID_CARD_DETAIL, { state: { fidCardNum } });
      } else {
        navigate(SCAN);
      }
      setFidCardSuccess(false);
    }, SUCCESS_SCREEN_DURATION);
  }, [navigate, user?._id, fidCardNum]);

  const closePopup = useCallback(() => {
    setPopup(false);
    setIsPaused(false);
    setFidCardSuccess(false);
  }, []);

  const handleScanResult = useCallback((scanResult) => {
    setFidelityCardId(scanResult.barcodes[0].data);
  }, []);

  useEffect(() => {
    trackPageViewFidScan({});
  }, [trackPageViewFidScan]);

  useEffect(() => {
    if (focusref?.current) {
      focusref.current.focus();
    }
    const timer = setTimeout(() => {
      setSeeReloadButton(true);
    }, RELOAD_BUTTON_DELAY);

    return () => clearTimeout(timer);
  }, [focusref]);

  useEffect(() => {
    const setupVideo = () => {
      const videoElem = document.querySelector('video.scandit-video');
      const overlay = document.querySelector('div.scandit.scandit-barcode-picker');

      if (videoElem) {
        videoElem.style.objectFit = 'contain';
      }
      if (overlay) {
        overlay.style.backgroundColor = '#818181';
      }
    };

    setupVideo();
    return () => {
      const videoElem = document.querySelector('video.scandit-video');
      if (videoElem) {
        videoElem.style.objectFit = 'cover';
      }
    };
  }, []);

  useEffect(() => {
    if (fidelityCardId) {
      processFidelityCard(fidelityCardId);
      setFidelityCardId(undefined);
    }
  }, [fidelityCardId, processFidelityCard]);

  useEffect(() => {
    const initializeScandit = async () => {
      try {
        await ScanditInit();
        setIsScanditLoaded(true);
      } catch (error) {
        console.error('Error in ScanditInit:: ', error);
      }
    };

    initializeScandit();
  }, []);

  const renderLoadingSpinner = () => (
    <div className="absolute z-50 w-screen h-screen" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="z-50 absolute left-2/4 top-[33%] -translate-x-2/4">
        <div className="lds-spinner white">
          {SPINNER_ELEMENTS.map((elementId) => (
            <div key={elementId} />
          ))}
        </div>
      </div>
    </div>
  );

  const renderPopup = () => (
    <>
      <div
        className={`backblur-fid-scan ${popup ? 'active' : ''}`}
        tabIndex={0}
        role="button"
        onClick={closePopup}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            closePopup();
          }
        }}
      />

      <div className={`popup-fid-scan-container ${popup ? 'active' : ''}`}>
        <div className="popup-fid-scan-invite" style={{ transform: 'translate(-50%, -50%)' }}>
          <div className={`popup-fid-scan-question ${fidCardSuccess ? '' : 'active'}`}>
            <img
              src={closeArrow}
              alt="close"
              className="popup-fid-scan-close"
              onClick={closePopup}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  closePopup();
                }
              }}
            />

            <h1>Tu es en mode invité</h1>
            <p>Ta carte ne sera pas sauvegardée pour la prochaine fois</p>

            <button type="button"
              className="popup-fid-scan-ok"
              onClick={() => navigateType('scan')}>
              Oui d'accord
            </button>
            <PrimaryButton
              label="Je créé mon compte"
              onClick={() => navigateType('account')}
            />
          </div>

          <div className={`popup-fid-scan-success ${fidCardSuccess ? 'active' : ''}`}>
            {fidCardSuccess && (
              <Lottie
                loop={false}
                autoplay={true}
                animationData={successLottie}
                height={70}
                width={70}
              />
            )}
            <h1>Carte ajoutée avec succès</h1>
          </div>
        </div>
      </div>
    </>
  );

  const renderHeader = () => (
    <div className="fixed z-50 flex items-center justify-center w-full h-16 px-4 top-4 left-4">
      <div
        onClick={goToCardScreen}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            goToCardScreen();
          }
        }}
        className="absolute left-0 z-10 cursor-pointer"
      >
        <IoIosArrowBack fill="white" size={22} />
      </div>
      <img src={companyImage || companyLogo || store?.logo} alt="logo" className="w-[125px]" />
    </div>
  );

  const renderManualInput = () => (
    <div className="flex items-center w-[90%] gap-4">
      <input
        type="text"
        placeholder="Numéros inscrits sur la carte fidélité"
        className="flex items-center geomanistBold text-4xl h-[50px] w-[80%] px-3 rounded-xl input"
        value={fidCardNum}
        onChange={(e) => setFidCardNum(e.target.value)}
      />
      <div
        className="bg-[#fffb26] h-[50px] w-[20%] flex justify-center items-center rounded-xl p-2"
        onClick={handleManualSubmit}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            handleManualSubmit();
          }
        }}
      >
        <IoMdCheckmark size={30} />
      </div>
    </div>
  );

  const renderManualButton = () => (
    <button type="button" className="saisie_manuelleFidscan" onClick={() => setManual(true)}>
      Saisie manuelle
      <img src={keyboard} alt="keyboard" className="keyboard" />
    </button>
  );

  const renderAdvantages = () => (
    <div className="px-8">
      <p className="mb-3 text-3xl text-white">Avantage</p>
      <small className="text-3xl text-white geomanistLight">
        Toutes les 10 visites, tu obtiens un coupon permettant de
        cagnotter 10% sur la prochaine visite réalisée dans les 10 jours.
      </small>
    </div>
  );

  const renderScanner = () => {
    if (!isScanditLoaded) return null;

    const commonProps = {
      preloadBlurryRecognition: true,
      preloadEngine: true,
      accessCamera: true,
      onScan: handleScanResult,
      pause: isPaused,
      onProcessFramze: true,
      scanSettings: getScanSettings(),
      videoFit: isPWA ? BarcodePicker.ObjectFit.COVER : ScanditSDK.BarcodePicker.ObjectFit.COVER,
      playSoundOnScan: true,
      enableCameraSwitcher: false,
      enablePinchToZoom: false,
      enableTapToFocus: true,
      enableTorchToggle: false,
      mirrorImage: true,
      targetScanningFPS: TARGET_SCANNING_FPS,
      zoom: 0,
    };

    if (isPWA) {
      return (
        <ScannerTest
          {...commonProps}
          sdk
          guiStyle={BarcodePicker.GuiStyle.VIEWFINDER}
          viewFinderArea={PWA_VIEWFINDER_AREA}
          setIsLoadingScanner={setIsLoadingScanner}
        />
      );
    }

    return (
      <Scanner
        {...commonProps}
        guiStyle={ScanditSDK.BarcodePicker.GuiStyle.VIEWFINDER}
        isLoading={isLoadingScanner}
        setIsLoading={setIsLoadingScanner}
        viewFinderArea={SEARCH_AREA_CONFIG}
      />
    );
  };

  return (
    <div>
      {user.userLoading || addFidCardLoading && renderLoadingSpinner()}

      <div ref={focusref} className="min-w-full min-h-full overflow-hidden text-white bg-black ScanFidelity">
        {seeReloadButton && (
          <ReloadPage
            ref={reloadPageRef}
            titre="Problème de scanneur ?"
            message="Clique ici pour actualiser"
            onClick={vidercache}
            position="top"
          />
        )}

        {renderPopup()}
        {renderHeader()}

        <div className="contenairestoreLogoFid_box">
          <div className="titre_scanfid">Associer ma carte de fidélité</div>
        </div>

        <div className="absolute_btn_fidelityscan">
          {manual ? renderManualInput() : renderManualButton()}
          {renderAdvantages()}
        </div>

        {setRecaptchaResultToken && (
          <div className="relative top-10" />
        )}

        {renderScanner()}
      </div>
    </div>
  );
};

export default FidScanScreen;
